import { isEmpty } from "js/common";
import React, { useEffect, useRef, useState } from "react";

/**
 * 숫자 입력시 컴마 표현. 소수점 및 음수 처리
 * @param {integer} decimalPlaceCnt 0 은 정수, > 0 은 소수점 자리수, < 0 이면 정수 자리수 반올림 (ex: -2이면, 152 -> 200. 118 -> 100)
 * 
* flow : 값 직접 입력시 - inputChange (자리수, 맨앞0등의 입력제한) -> 부모값이 변경되므로 useEffect[value]에서 값을 그대로 view에 세팅 -> blur시에 format 처리
*        다른곳에서 세팅시 - useEffect[value] format 처리 해서 view에 세팅. format처리한 viewValue와 그냥 value가 다른 경우 value를 다시 세팅.
 */
export default function Float({ onChange, value, name, style, className, disabled, readOnly, decimalPlaceCnt = 0, maxVal, minVal }) {

    const [viewValue, setViewValue] = useState("");
	const inputView = useRef(null);
    const [cursorPosition, setCursorPosition] = useState(-1);
	const [inputStyle, setInputStyle] = useState(style);

    useEffect(() => {
		if(document.activeElement === inputView.current) {
			// 직접 값을 입력하는 경우
			// 아무것도 안함. 이미 처리 되어 있음.
		} else {
			// 값이 세팅되는 경우
			const formattedValue = getFormattedValue(value);
			setViewValue(formattedValue);	// format 설정해서 세팅
		}
    }, [value]);
	
	// viewValue변경시 cursor위치 재설정
	useEffect(() => {
		const newPosition = inputView.current.value.length - cursorPosition;
		setInputViewCursorPosition(newPosition);
	}, [viewValue]);

	useEffect(() => {
		// 너비, 높이는 설정하지 않고, 겉의 div 크기를 따르는걸로..
		// style이 겉의 div, 안의 input에 모두 적용되는 관계로 width,height에 %가 들어가면 막 작아지는 것을 방지 하기 위함.
		const tmpStyle = {...style};
		delete tmpStyle.width;
		delete tmpStyle.height;
		setInputStyle(tmpStyle);
	}, [style]);

	// 커서 위치 설정
	const setInputViewCursorPosition = (position) => {
		setTimeout(function() {
			// 원래 커서 포지션으로 이동
			inputView.current?.setSelectionRange(position, position);
		}, 1);	// 왜인지 setTimeout으로 걸어줘야 정상 동작
	}

	// 입력값 변경시
	const inputChange = (e) => {
		const val = e.target.value;

		const curPosition = val.length - e.target.selectionStart;	// 커서 위치. 뒤에서부터 계산
		setCursorPosition(curPosition);
		
		setTimeout(() => {
			if(viewValue.replace(/,/g, "") === val.replace(/,/g, "")) {
				// 숫자는 변화 없음(콤마 지운것). 여기서 콤마 다시 계산하거나 하면 계속 안지워지거나 하는 문제가 발생.
				setViewValue(val);
			} else {
				// 값이 변화한거면 입력제한값 구해서 value에 세팅하고, 콤마 찍어서 view에 세팅하고.
				// const inputLimitVal = getInputLimitValue(val);
				const inputLimitVal = getInputLimitValue(val, curPosition);
				if(inputLimitVal === "invalid") {
					const newPosition = viewValue.length - curPosition;	// 원래 값 기준으로 커서 위치 다시 설정
					setInputViewCursorPosition(newPosition);
					return;
				}
				// 부모에게 전달
				if(onChange) {
					const target = inputView.current;
					target.value = inputLimitVal;
					onChange({target: target});
				}
				// onValueChange && onValueChange(inputLimitVal);

				setViewValue(getCommaNumber(inputLimitVal));	// viewValue는 콤마 찍어서서
			}
		}, 0);
		
	}


	// 입력 제한 값 (입력중에 반올림 이런건 안하고, 그냥 자리수 제한이나 맨앞에 0제거 이런거만..)
	const getInputLimitValue = (val, curPosition) => {
		let stringValue = String(val);
		// 음수기호
		let negativeSign = "";
		if(stringValue.startsWith("-")) {
			negativeSign = "-";
			stringValue = stringValue.replace("-", "");
		}
		
		// 앞에 있는 0 제거
		while(stringValue.length > 1 && stringValue.indexOf("0") === 0 && stringValue.indexOf("0.") !== 0) {
			stringValue = stringValue.substring(1);
		}

		// 자리수나 소수점자리수 등 제한
		if(decimalPlaceCnt === 0) {
			stringValue = getOnlyNumber(stringValue.split(".")[0]);
		} else if(decimalPlaceCnt < 0) {
			stringValue = getOnlyNumber(stringValue.split(".")[0]);	// 입력할때는 뭐 입력하게 해줘야지..
		} else {
			let tmpValue = getOnlyNumber(stringValue.split(".")[0]);
			if(stringValue.includes(".")) {
				if(tmpValue === "") tmpValue = "0";	// .부터 시작하면 앞에 0붙여주기
				let decimal = getOnlyNumber(stringValue.split(".")[1]);
				if(decimal.length > decimalPlaceCnt) {
					setCursorPosition(curPosition - (decimal.length - decimalPlaceCnt));
					decimal = decimal.substring(0, decimalPlaceCnt);
				}
				tmpValue += "." + decimal;
				// tmpValue += "." + getOnlyNumber(stringValue.split(".")[1]).substring(0, decimalPlaceCnt);
			}
			stringValue = tmpValue;
		}

		if(negativeSign + stringValue !== "") {
				
			// 최대값 최소값 체크
			if(!isEmpty(maxVal) && Number(negativeSign + stringValue) > maxVal) {
				// 최대값 초과시
				inputView.current.style.color = "red";
			} else if(!isEmpty(minVal) && Number(negativeSign + stringValue) < minVal) {
				// 최소값 미만시
				inputView.current.style.color = "red";
			} else {
				// 정상 범위
				inputView.current.style.color = "inherit";
			}
		} else {
			// 입력 값 없음
			inputView.current.style.color = "inherit";
		}

        // const formattedValue = negativeSign + stringValue.replace(/,/g, ""); // 콤마를 제거한 숫자 값
        const formattedValue = negativeSign + stringValue; // 콤마를 제거한 숫자 값
		return formattedValue;
	}


	// 제한, 콤마, 자리수, 반올림 등 다한 값.
	const getFormattedValue = (value) => {
		if(value === "") {
			// 입력값이 없으면 공백
			return "";
		}
		let stringValue = String(value);
		let negativeSign = "";	// 음수기호
		let integer = "";	// 정수부분
		let decimal = "";	// 소수부분

		// 음수기호
		if(stringValue.startsWith("-")) {
			negativeSign = "-";
			stringValue = stringValue.replace("-", "");
		}
		
		// 앞에 있는 0 제거
		while(stringValue.length > 1 && stringValue.indexOf("0") === 0 && stringValue.indexOf("0.") !== 0) {
			stringValue = stringValue.substring(1);
		}
		
		if(decimalPlaceCnt <= 0) {
			// 정수만 처리하는 경우
			const baseValue = Math.pow(10, Math.abs(decimalPlaceCnt));
			integer = Math.round(getOnlyNumber(stringValue.split(".")[0]) / baseValue) * baseValue;
			decimal = "";

		} else {
			// 소수점 자리수가 있으면
			// 정수부/소수부 분리
			integer = getOnlyNumber(stringValue.split(".")[0]);

			if(stringValue.indexOf(".") > -1) {
				decimal = getOnlyNumber(stringValue.split(".")[1]);

				if(decimalPlaceCnt > 0) {
					// 소수점 자리수가 0보다 크면
					if(decimal.length > decimalPlaceCnt) {
						const baseValue = Math.pow(10, Math.abs(decimalPlaceCnt));
						decimal = "0." + decimal;
						decimal = Math.round(Number(decimal) * baseValue);
						decimal = String(decimal).padEnd(decimalPlaceCnt, "0");
					}

					// 원래 있던 소수점 붙이기
					decimal = "." + decimal;

				} else {
					decimal = "";
				}

			}

		}

		integer = getCommaNumber(integer);
		const formattedValue = negativeSign + integer + decimal;
		return formattedValue;

	}
	
	// 포커스 나가면 format 맞춰서 값 다시 세팅
	const onBlur = (e) => {
		let { value } = e.target;
		
		// 최대값, 최소값에 맞춰서 값 변경
		if(value !== "") {
			// 음수기호
			let negativeSign = "";
			if(value.startsWith("-")) {
				negativeSign = "-";
			}
			value = negativeSign + getOnlyNumber(value);
			// focus 나가면
			// 최대값 최소값 처리
			if(!isEmpty(maxVal) && Number(value) > maxVal) {
				// 최대값 초과시
				value = maxVal;
			} else if(!isEmpty(minVal) && Number(value) < minVal) {
				// 최소값 미만시
				value = minVal;
			}
			// 최대값 최소값 맞췄으니 색상은 다시 원래대로..
			inputView.current.style.color = "inherit";
		}
		
		// format 처리
		setViewValue(getFormattedValue(value));
		// onChange && onChange(getFormattedValue(value).replace(/,/g, ""));
		if(onChange) {
			const target = inputView.current;
			target.value = getFormattedValue(value).replace(/,/g, "");
			onChange({target: target});
		}
		// onValueChange && onValueChange(getFormattedValue(value).replace(/,/g, ""));
	}

	// max, min value 변경시 처리
	useEffect(() => {
		let value = inputView.current.value
		if(value === "") return;
		// 음수기호
		let negativeSign = "";
		if(value.startsWith("-")) {
			negativeSign = "-";
		}
		value = negativeSign + getOnlyNumber(value);

		if(!isEmpty(maxVal) && Number(value) > maxVal) {
			// 최대값 초과시
			value = maxVal;
		} else if(!isEmpty(minVal) && Number(value) < minVal) {
			// 최소값 미만시
			value = minVal;
		}
		setViewValue(getFormattedValue(value));
		if(onChange) {
			const target = inputView.current;
			target.value = getFormattedValue(value).replace(/,/g, "");
			onChange({target: target});
		}
	}, [maxVal, minVal]);

	const getCommaNumber = (num) => {
		const noCommaVal = String(num).replace(/,/g, "");
		let rtnVal = noCommaVal.split(".")[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		if(noCommaVal.includes(".")) {
			rtnVal += ".";
			// rtnVal += noCommaVal.split(".")[1].replace(/\B(?=(\d{3})+(?!\d))/g, ",");;
			rtnVal += noCommaVal.split(".")[1];
		}
		return rtnVal;
	}

	const getOnlyNumber = (num) => {
		let rtnVal = String(num.split(".")[0]).replace(/\D/g, "");
		if(num.includes(".")) {
			rtnVal += ".";
			rtnVal += String(num.split(".")[1]).replace(/\D/g, "");
		}

		return rtnVal;

	}

    return (
        // <div className="formatted-input">
        <div style={style} >
            <input
                autoComplete="off"
                type="text"
                value={value} // 실제 입력된 숫자 값을 표시
                onChange={() => {}} // 입력된 값이 변경되지 않도록 빈 함수를 사용
                style={{ display: "none" }} // 화면 숨김
            />
            <input
                autoComplete="off"
                type="text"
				name={name ?? ""}
                value={viewValue} // 콤마로 표시되는 값
                onChange={inputChange} // 값 변경 처리
                style={inputStyle}
				className={(className ?? "")}
				ref={inputView}
				onBlur={onBlur}
				readOnly={readOnly ?? false}
				disabled={disabled ?? false}
				inputMode="numeric"
            />
        </div>
    );
}